// QR Kiosk v2 — KV2-3 photo-first staged-file grid. `photo` variant (create, 3-col
// 84px tiles: camera + thumbnails + amber TOO-BIG + "+"); `row` variant (solve, 2-col
// 52px: add-photo + chip cells). Staged data-URI files are NON-link previews (Chrome
// blocks top-frame data: navigation — KV2-3-e); fetched URL-content files reuse
// ReportFileChip in the row variant. Camera tile narrows via accept/capture; "+" tile
// is unrestricted (KV2-3-d). Tokens only; ✕ remove is a spec-sanctioned inline SVG.
import React, { useMemo, useState } from "react";
import { Glyph, GD } from "../shared/glyphs";
import { ServiceReportFile } from "../../models/serviceReportsModels";
import { isImageFileName } from "./reportUtils";
import { ReportFileChip } from "./ReportFileChip";
import { PhotoCarousel } from "./PhotoCarousel";
import { RejectedFile } from "./useKioskFiles";
import useTranslation from "../../../shared/hooks/useTranslation";

export interface FileTileGridProps {
  files: ServiceReportFile[];
  onAdd: (list: FileList) => void;
  onRemove: (index: number) => void;
  rejected: RejectedFile | null;
  onDismissRejected: () => void;
  variant: "photo" | "row";
  inputId: string;
}

const camDash = "1.5px dashed var(--tier-accent-line)";
const camBg = "var(--tier-accent-soft)";
const camFg = "var(--tier-accent)";
const amberBd = "1px dashed color-mix(in oklab, var(--amber) 55%, var(--border))";
const amberBg = "color-mix(in oklab, var(--amber) 8%, var(--card))";
const amberInk = "color-mix(in oklab, var(--amber) 75%, var(--ink))";

function RemoveButton({ onClick, style }: { onClick: () => void; style?: React.CSSProperties }) {
  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  };
  return (
    <button
      type="button"
      aria-label="remove"
      onClick={stop}
      style={{ position: "absolute", top: 0, right: 0, padding: 4, border: "none", background: "transparent", cursor: "pointer", display: "grid", placeItems: "center", ...style }}
    >
      <span style={{ width: 26, height: 26, borderRadius: 999, background: "color-mix(in oklab, var(--ink) 55%, transparent)", color: "var(--card)", display: "grid", placeItems: "center" }}>
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
          <path d="M6 6 L18 18" />
          <path d="M18 6 L6 18" />
        </svg>
      </span>
    </button>
  );
}

export function FileTileGrid({ files, onAdd, onRemove, rejected, onDismissRejected, variant, inputId }: FileTileGridProps) {
  const { translate } = useTranslation("public");
  // §7.3 edit-ticket wiring: image tiles open a full-screen PhotoCarousel with staged
  // delete (carousel index → files index via imageMap). Only meaningful in the photo variant.
  const [viewerIdx, setViewerIdx] = useState<number | null>(null);
  const imageMap = useMemo(
    () => files.map((f, i) => ({ f, i })).filter(({ f }) => isImageFileName(f.name) && !!f.content),
    [files]
  );
  const onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) onAdd(e.target.files);
    e.target.value = "";
  };

  const hiddenInputs = (
    <>
      <input type="file" id={`${inputId}-cam`} accept="image/*" capture="environment" style={{ display: "none" }} onChange={onInput} />
      <input type="file" id={`${inputId}-any`} multiple style={{ display: "none" }} onChange={onInput} />
    </>
  );

  // ── photo variant (create) ──
  if (variant === "photo") {
    return (
      <div>
        {hiddenInputs}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <label
            htmlFor={`${inputId}-cam`}
            style={{ height: 84, borderRadius: 11, border: camDash, background: camBg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, color: camFg, cursor: "pointer" }}
          >
            <Glyph d={GD.cam} size={22} />
            <span style={{ fontSize: 10.5, fontWeight: 600 }}>{translate("kioskV2TakePhoto")}</span>
          </label>

          {files.map((f, i) => {
            const isImg = isImageFileName(f.name);
            // Image-with-content tile → tappable (opens carousel at its position); ✕ quick-
            // remove stays (carousel-delete is discoverable, ✕ is the fast path). Others inert.
            if (isImg && f.content) {
              return (
                <button
                  key={`f-${i}`}
                  type="button"
                  onClick={() => setViewerIdx(imageMap.findIndex((x) => x.i === i))}
                  style={{
                    height: 84,
                    borderRadius: 11,
                    border: "1px solid var(--border)",
                    overflow: "hidden",
                    position: "relative",
                    padding: 0,
                    width: "100%",
                    cursor: "zoom-in",
                    backgroundImage: `url(${f.content})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <RemoveButton onClick={() => onRemove(i)} />
                </button>
              );
            }
            return (
              <div key={`f-${i}`} style={{ height: 84, borderRadius: 11, border: "1px solid var(--border)", overflow: "hidden", position: "relative", display: "grid", placeItems: "center", background: "color-mix(in oklab, var(--faint) 18%, transparent)" }}>
                <div style={{ display: "grid", placeItems: "center", gap: 3, padding: "0 6px", maxWidth: "100%" }}>
                  <span style={{ color: "var(--muted)" }}><Glyph d={GD.doc} size={18} /></span>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--muted)", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                </div>
                <RemoveButton onClick={() => onRemove(i)} />
              </div>
            );
          })}

          {rejected && (
            <button
              type="button"
              onClick={onDismissRejected}
              style={{ height: 84, borderRadius: 11, border: amberBd, background: amberBg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3, padding: "0 6px", textAlign: "center", cursor: "pointer" }}
            >
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, color: amberInk }}>{translate("kioskV2TooBig")}</span>
              <span style={{ fontSize: 9.5, color: "var(--muted)" }}>{`${rejected.name} · ${rejected.sizeMb} MB`}</span>
              <span style={{ fontSize: 9.5, color: "var(--muted)" }}>{translate("kioskV2MaxFilePerFile")}</span>
            </button>
          )}

          <label
            htmlFor={`${inputId}-any`}
            style={{ height: 84, borderRadius: 11, border: "1px dashed var(--border)", display: "grid", placeItems: "center", color: "var(--faint)", fontSize: 20, cursor: "pointer" }}
          >
            +
          </label>
        </div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, color: "var(--faint)", marginTop: 5 }}>{translate("kioskV2MaxFilePerFile")}</div>
        {viewerIdx != null && (
          <PhotoCarousel
            images={imageMap.map(({ f }) => ({ src: f.content!, name: f.name }))}
            initialIndex={viewerIdx}
            onClose={() => setViewerIdx(null)}
            onDelete={(ci) => onRemove(imageMap[ci].i)}
          />
        )}
      </div>
    );
  }

  // ── row variant (solve) ──
  return (
    <div>
      {hiddenInputs}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <label
          htmlFor={`${inputId}-cam`}
          style={{ height: 52, borderRadius: 11, border: camDash, background: camBg, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: camFg, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
        >
          <Glyph d={GD.cam} size={18} />
          {translate("kioskV2AddPhoto")}
        </label>

        {files.length === 0 ? (
          <label
            htmlFor={`${inputId}-any`}
            style={{ height: 52, borderRadius: 11, border: "1px dashed var(--border)", display: "grid", placeItems: "center", color: "var(--faint)", fontSize: 20, cursor: "pointer" }}
          >
            +
          </label>
        ) : (
          files.map((f, i) => {
            const isData = typeof f.content === "string" && f.content.startsWith("data:");
            if (!isData && f.content) {
              return (
                <div key={`f-${i}`} style={{ position: "relative" }}>
                  <ReportFileChip file={f} />
                  <RemoveButton onClick={() => onRemove(i)} style={{ top: "50%", right: 0, transform: "translateY(-50%)" }} />
                </div>
              );
            }
            const isImg = isImageFileName(f.name);
            return (
              <div
                key={`f-${i}`}
                style={{ position: "relative", display: "flex", alignItems: "center", gap: 9, height: 46, padding: "0 34px 0 11px", borderRadius: 10, border: "1px solid var(--border)", background: "var(--bg-soft)", minWidth: 0 }}
              >
                <span style={{ width: 28, height: 28, borderRadius: 6, background: isImg ? "color-mix(in oklab, var(--faint) 22%, transparent)" : "var(--card)", border: "1px solid var(--border-soft)", display: "grid", placeItems: "center", color: "var(--muted)", flexShrink: 0 }}>
                  <Glyph d={isImg ? GD.cam : GD.doc} size={14} />
                </span>
                <span style={{ fontSize: 12, fontWeight: 500, color: "var(--ink-2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name}</span>
                <RemoveButton onClick={() => onRemove(i)} style={{ top: "50%", right: 0, transform: "translateY(-50%)" }} />
              </div>
            );
          })
        )}
      </div>
      {rejected && (
        <button
          type="button"
          onClick={onDismissRejected}
          style={{ width: "100%", marginTop: 8, borderRadius: 11, border: amberBd, background: amberBg, padding: "8px 12px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", textAlign: "left" }}
        >
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, fontWeight: 700, color: amberInk, flexShrink: 0 }}>{translate("kioskV2TooBig")}</span>
          <span style={{ fontSize: 10.5, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {`${rejected.name} · ${rejected.sizeMb} MB · ${translate("kioskV2MaxFilePerFile")}`}
          </span>
        </button>
      )}
    </div>
  );
}

export default FileTileGrid;
